<template>
  <div
    class="feature"
    :class="[{ 'is-link': feature.to }, direction, theme]"
    @click="feature.to ? push(feature.to) : undefined"
  >
    <div class="head">
      <UiComponentImage
        v-if="feature.icon && iconIsAnImage"
        :alt="feature.icon.alt"
        :expected-size="20"
        :height="feature.icon.height"
        :source="feature.icon.url"
        :width="feature.icon.width"
      />
      <UiComponentIcon
        v-else-if="feature.icon"
        :type="feature.icon.name"
      />
    </div>
    <div class="body">
      <NuxtLink
        v-if="feature.to"
        :to="feature.to"
      >
        <UiComponentHeading :level="level">
          {{ feature.title }}
        </UiComponentHeading>
      </NuxtLink>
      <UiComponentHeading
        v-else
        :level="level"
      >
        {{ feature.title }}
      </UiComponentHeading>
      <UiComponentParagraph :class="{ push: hasSlot }">
        {{
          feature.description
        }}
      </UiComponentParagraph>
      <AtomButton
        v-if="hasSlot"
        type="text"
        @click="emit('button:click', $event)"
      >
        <slot /><UiComponentIcon type="arrow-right" />
      </AtomButton>
    </div>
  </div>
</template>

<script setup>
// Composables
const { push } = useRouter()

// Emitter
const emit = defineEmits([
  'button:click',
])

// Props
const props = defineProps({
  direction: {
    default() {
      return 'column'
    },
    required: false,
    type: String,
    validator(value) {
      return ['column', 'row'].indexOf(value) !== -1
    },
  },
  feature: {
    required: true,
    type: Object,
  },
  level: {
    default() {
      return 3
    },
    required: false,
    type: Number,
    validator(value) {
      return [2, 3, 4, 5].indexOf(value) !== -1
    },
  },
  theme: {
    default() {
      return 'black'
    },
    required: false,
    type: String,
    validator(value) {
      return ['black', 'white'].indexOf(value) !== -1
    },
  },
})

// Composable
const slots = useSlots()

// Computed Properties
const hasSlot = computed(() => {
  const nodes = slots && slots.default && slots.default()
  return nodes && nodes.length > 0
})

const iconIsAnImage = computed(() => {
  return !!props.feature.icon?.url
})
</script>

<style lang="postcss" scoped>
.feature {
  @apply flex w-full;

  &.is-link {
    @apply cursor-pointer;
  }

  &.blizzard {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-blizzard-700 !important;
      }
    }

    .head {
      @apply bg-blizzard-100 !important;
    }
  }

  &.grey-blue {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-grey-blue-700 !important;
      }
    }

    .head {
      @apply bg-grey-blue-100 !important;
    }
  }

  &.livestorm-blue {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-livestorm-blue-700 !important;
      }
    }

    .head {
      @apply bg-livestorm-blue-100 !important;
    }
  }

  &.live-red {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-live-red-700 !important;
      }
    }

    .head {
      @apply bg-live-red-100 !important;
    }
  }

  &.sirocco {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-sirocco-700 !important;
      }
    }

    .head {
      @apply bg-sirocco-100 !important;
    }
  }

  &.sunburst-red {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-sunburst-red-700 !important;
      }
    }

    .head {
      @apply bg-sunburst-red-100 !important;
    }
  }

  &.winter-green {
    &:hover {
      .h2,
      .h3,
      .h4,
      .h5,
      .button,
      i {
        @apply text-winter-green-700 !important;
      }
    }

    .head {
      @apply bg-winter-green-100 !important;
    }
  }

  &.column {
    @apply flex-col;

    .head + .body {
      @apply mt-6;
    }
  }

  &.row {
    @apply flex-col;

    @screen xl {
      @apply flex-row;
    }

    .head + .body {
      @apply mt-6;

      @screen xl {
        @apply mt-0 ml-6;
      }
    }

    .head {
      @apply flex-shrink-0;
    }
  }

  &.white {
    .head {
      @apply bg-grey-blue-800;
    }

    .h2,
    .h3,
    .h4,
    .h5 {
      @apply font-medium text-white;
    }

    p {
      @apply mt-4 text-grey-blue-600;
    }
  }

  .head {
    @apply flex justify-center items-center h-20 w-20 bg-grey-blue-100 text-winter-green-900 flex-shrink-0;
    border-radius: 24px;

    i {
      @apply text-2xl leading-none;
    }

    picture {
      @apply rounded-none;
    }
  }

  .body {
    @apply flex flex-col flex-grow h-auto;

    .h2,
    .h3,
    .h4,
    .h5 {
      @apply font-medium text-xl leading-7;

      @screen mdminus {
        @apply text-2xl leading-8;
      }

      @screen mdplus {
        @apply text-2xl leading-8;
      }
    }

    p {
      @apply mt-4 text-grey-blue-600;

      &.push {
        @apply mb-5;
      }
    }

    .button {
      @apply mt-auto justify-start px-0;
    }
  }

  .head,
  .body .h2,
  .body .h3,
  .body .h4,
  .body .h5,
  .body .button {
    @apply transition duration-300 ease-out;
  }
}

a.feature {
  &:hover {
    .head {
      @apply bg-livestorm-blue-100 text-livestorm-blue-700;
    }

    .body {
      .h2,
      .h3,
      .h4,
      .h5,
      .button {
        @apply text-livestorm-blue-700;
      }
    }
  }
}
</style>
